import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetGenericEvent} from 'features/core/shared/widget/event/widget-generic-event';
import {MvsWidgetFormGenericEventConstants} from
'features/core/shared/widget/component/runtime/implementations/mvs-widget-form/mvs-widget-form-generic-event.constants';
import {MvsFormGroup} from 'features/core/shared/logic/form/mvs-form-group';
import {MvsFormDto} from 'features/core/shared/dto/form/dto/mvs-form.dto';
import {MvsFormFieldDto} from 'features/core/shared/dto/form/dto/mvs-form-field.dto';
import {
    MvsFormValueListEntryDto
} from 'features/core/shared/dto/form/dto/mvs-form-value-list-entry.dto';
import {MvsFormFieldHelper} from 'features/core/shared/form/field/service/mvs-form-field-helper.service';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsCrudService} from 'features/core/shared/service/crud/mvs-crud.service';
import {DtoDetail} from 'features/core/shared/dto/dto.detail';
import {DtoList} from 'features/core/shared/dto/dto.list';
import {ObjectRequestList} from 'features/core/shared/dto/object/request/object-request-list';
import {Sorting} from 'features/core/shared/misc/sorting';
import {PagingDto} from 'features/core/shared/dto/model/paging.dto';
import {CpProvisionSimulationService} from '../../../service/api/cp-provision-simulation.service';
import {ProvisionTypeDto} from '../../../model/dto/entity/provision-type.dto';
import {
    ProvisionSimulationAgentDto,
    ProvisionSimulationDimensionDto,
    ProvisionSimulationFormDto,
    ProvisionSimulationRequestDto,
    ProvisionSimulationResultDto
} from '../../../model/dto/api/provision-simulation.dto';
import {ProvisionSimulationState} from '../../../enum/provision-simulation-state.enum';
import {ProvisionAgentRoleEnum} from '../../../enum/provision-agent-role.enum';
import {ProvisionPaymentFrequency} from '../../../enum/provision-payment-frequency.enum';
import {PROVISION_PAYMENT_FREQUENCY_OPTIONS} from '../../../enum/provision-payment-frequency-label';
import {
    formatProvisionSimulationValue
} from '../../../service/domain/cp-simulation-figure-format';
import {
    provisionSimulationChipStyle,
    provisionSimulationResolvedState
} from '../../../service/domain/cp-simulation-presentation';
import {
    CP_SIMULATION_AGENT_OPTIONS_KEY,
    CpProvisionSimulationDimensionOptionsService
} from '../../../service/ql/cp-provision-simulation-dimension-options.service';
import {
    ProvisionSimulationDimensionOptionsRequestDto
} from '../../../model/dto/api/provision-simulation-dimension-options-request.dto';
import {
    ProvisionSimulationDimensionOptionsResultDto
} from '../../../model/dto/api/provision-simulation-dimension-options-result.dto';

/**
 * Provisionssimulation — the fourth navItem of the provision dashboard
 * (`/#/cp/dashboard/simulation`).
 *
 * ── WHAT WAS ORDERED
 *
 * "Wir brauchen im cp/dashboard eine simulationsfunktion wo ich die ganzen Dimensionsdaten sowie
 * den Vertragsstart eingeben kann sowie die Berater, damit wir die Provision simulieren können. Die
 * Anwender wollen das haben um die Berechnungen genau testen zu können. **Hier brauchen wir dann
 * detaillierte Informationen wie alles berechnet wurde.**" The last sentence is the product: the
 * amount is the smaller half, the Rechenprotokoll is the ordered one. This component captures the
 * inputs and hands the answer to {@link CpProvisionSimulationEntitlementsComponent} (what came
 * out) and {@link CpProvisionSimulationProtocolComponent} (how it came about).
 *
 * ── THE FORM FOLLOWS THE CONFIGURATION, NOT A HARD-WIRED MASK (Auflage D1)
 *
 * `GET …/simulationForm` answers with the nine contract dimensions and, per dimension, whether this
 * Provisionsart CONSTRAINS it anywhere (`occupied`). Occupied dimensions become visible fields;
 * the rest go into a collapsed second form. The field list is therefore DATA — a different tenant
 * with different configuration gets a different form without a line changing here. The concrete
 * failure this avoids is measured: 86 of the 180 active assignments of the reference tenant
 * restrict the ORIGIN (`contractOriginType`), and three of its four components hang off it, so a
 * form that buries the origin answers the first demonstration with the catch-all row and reads as a
 * calculation error.
 *
 * ── FORM MECHANISM: TRANSIENT FORM WIDGET, NEVER A HAND-WRITTEN FORM
 *
 * Same construction as this module's "Manuelle Buchung" dialog and as `wf-process-start`:
 *
 *   const form = new MvsFormDto();  form.formFields = { … MvsFormFieldDto … };
 *   WidgetFactory.createWidget(alias, name, 'form', 'transient', 'transient', objectType,
 *                              WidgetDataParam.create('form', form));
 *   <mvs-widget [widgetData]="…" (onGenericEvent)="…">
 *
 * The fields are rendered by the framework through the registered input components; the captured
 * values come back on `EVENT_FORM_VALUE_CHANGE` together with `MvsFormGroup.status`. There is no
 * hand-written form HTML anywhere in this component, and there is no `mvs-crud-object-generic`
 * either: a simulation has no business object and no CRUD write path — the request goes to a custom
 * endpoint and the form is only the capture.
 *
 * ── FKEY FIELDS: SEARCHABLE DROPDOWNS RESOLVED VIA QL — NOT THE FormService
 *
 * A `ManyToOne` field WITHOUT `uiInputControl` renders as `flexibleSearch`
 * (`mvs-form-field.component.ts:437`) — a server-side type-ahead over ids nobody memorises. Every
 * FKEY field except `contractDtoId` (mode B, `cm.Contract`, 532 434 rows — kept on the implicit
 * `flexibleSearch` unconditionally, capability-matrix S-3) is instead given an EXPLICIT
 * `uiInputControl`, resolved ONCE per component lifetime by
 * {@link CpProvisionSimulationDimensionOptionsService.loadOptions} in {@link loadDimensionOptions}:
 * a complete `select` dropdown with a search filter when the entity's row count is below the
 * platform transfer threshold (nine of the ten FKEY fields today), `autoCompleteFkey` when it is at
 * or above it (only `cm.Partner`, 2 209 rows, today — the decision is measured on every load, never
 * a hardcoded per-entity mapping, so it self-corrects as the data grows). This is an explicit user
 * decision (capability-matrix FD-3, "Bitte das für cp direkt via QL umsetzen nicht den FormService
 * anpassen!"): the platform's own value-list builder in `core/form/FormService.java` does the same
 * job server-side and is deliberately NOT touched by this change.
 *
 * ── PRE-FILL GOES THROUGH THE WIDGET'S OWN HOOK
 *
 * A transient form is built without a data object, so `MvsFormControlService.buildFormGroup` has
 * nothing to take initial values from (its `contextValue` branch HIDES the field it fills, which is
 * the opposite of a pre-fill). The widget's own hook is `EVENT_NEW_FORM_GROUP`, raised exactly once
 * per built form group AFTER the value subscription is in place: the defaults are patched into the
 * form group there, so they flow back through `EVENT_FORM_VALUE_CHANGE` like any user entry and
 * take part in the validity gate. Only EMPTY controls are filled — a rebuild never overwrites a
 * value that was already there.
 *
 * ── MODE A CAPTURES THE SINGLE PRICE FIELDS, NOT ONE PREMIUM
 *
 * Mode A used to send exactly one amount (`premiumAmount`), which the server wrote into gross, net
 * and payment amount at once, with fee and tax rate left empty — so the two net premium price
 * references had nothing to read and answered nothing at all. The main form therefore carries an
 * OPTIONAL price block (`priceGross`, `feeAmount`, `paymentAmount`, `tax`) on top of the premium.
 * Every one of the four may stay empty; an empty field keeps the previous fallback for exactly that
 * value, so an untouched block reproduces the historic request. `tax` is a PERCENTAGE RATE and is
 * labelled and described as one — a rate read as an amount is a wrong net premium nothing reports.
 * The Vorvertrag stays with mode B: mode A depicts new business only.
 *
 * ── DERIVED DIMENSIONS FOLLOW THEIR SOURCE — A LOOKUP, NOT A CALCULATION
 *
 * Three dimensions are implied by another one: the Sparte (`group`) by the Vertragsart
 * (`cm.ContractType.group`), the Vertragsart by the Produkt (`cm.Product.contractType`) and the
 * Basiskategorie by the Basisart (`cm.ContractBaseType.category`). The Kategorie is deliberately
 * NOT among them — measured, 97 % of the real contracts would contradict their contract type's
 * category. Which edges exist is DATA: the form DTO names them per dimension as `derivedFromKey`,
 * and this component builds its locks from that property alone — a backend without it simply
 * yields a form without locks. The moment a source is chosen, the source entity is READ through
 * its own CRUD service, the implied id is set into the target field and the field is switched to
 * read-only; when the source is deselected the target is released and the value this component
 * set is taken back. A chain (Produkt → Vertragsart → Sparte) settles itself through the same
 * value-change events. The server derives the same edges in mode A and refuses a contradiction
 * with HTTP 400 naming both values — nothing is computed here, and the answer's
 * `dimensions[].origin` says `DERIVED` for these.
 *
 * ── THE THREE FORMS STAY MOUNTED
 *
 * Mode A main, mode A "Weitere Dimensionen" and mode B live side by side and are shown or hidden by
 * a CSS class, never by `@if`. An `@if` would destroy the widget, and a transient form has no
 * server to reload its values from — every entry made before the toggle would be gone. Switching
 * mode is a change of visibility, not a rebuild of the form.
 *
 * ── NOTHING IS CALCULATED IN THE BROWSER
 *
 * The simulation IS the productive calculation chain, fed with transient inputs and held back from
 * writing. This component assembles a request, renders an answer, and formats numbers for German
 * eyes. It sums nothing, converts nothing, derives no winner and re-orders no list.
 *
 * The one read it does make is a LOOKUP, not a calculation: a current backend fills the answer's
 * `dimensions[].valueId` AND `dimensions[].value` (the resolved name) in BOTH modes, from the very
 * record the engine calculated with — in mode B that is the real contract row. Only a backend
 * older than the name field sends an id WITHOUT a name, and for exactly that deploy-window case
 * the component fetches the display name of the id from the dimension's own CRUD service, the
 * lookup Spezifikation §2.3 permits by name. `dimension.value` is preferred whenever it is sent,
 * so on a current backend the lookup never runs.
 *
 * ── "SIMULIERT" ALREADY MEANS SOMETHING ELSE IN THIS MODULE
 *
 * `provisionSimulatedBalance` is the account balance INCLUDING pre-booked CALCULATED entries and is
 * on screen on the account, balance and KPI panes. The notice above the result names the difference
 * in one sentence — it comes from the server, so both sides say the same thing.
 */
@Component({
    selector: 'cp-provision-simulation',
    templateUrl: './cp-provision-simulation.component.html',
    styleUrls: ['./cp-provision-simulation.component.scss'],
    standalone: false
})
export class CpProvisionSimulationComponent implements OnInit {

    initialized: boolean;

    /** True while the form definition or a simulation is in flight. */
    busy: boolean;

    /** Mode A — a hypothetical contract from the entered dimensions. */
    static readonly MODE_HYPOTHETICAL: string = 'A';

    /** Mode B — an existing `cm.Contract`, recalculated from scratch. */
    static readonly MODE_EXISTING: string = 'B';

    /**
     * Term pre-fill when the form definition carries none — the same 36 months the server
     * delivers as `defaultTermMonths` (`40 §2`). Only reached against a backend older than the field.
     */
    static readonly FALLBACK_TERM_MONTHS: number = 36;

    /** The Provisionsarten that can be simulated. */
    provisionTypes: ProvisionTypeDto[] = [];

    /** The chosen Provisionsart; the form is loaded for it. */
    selectedProvisionTypeId: number;

    /** The form definition of the chosen Provisionsart. */
    simulationForm: ProvisionSimulationFormDto;

    /** Currently active mode. */
    mode: string = CpProvisionSimulationComponent.MODE_HYPOTHETICAL;

    modeOptions: { label: string, value: string, icon: string }[] = [
        {
            label: 'Angenommener Vertrag',
            value: CpProvisionSimulationComponent.MODE_HYPOTHETICAL,
            icon: 'fa-regular fa-pen-to-square'
        },
        {
            label: 'Bestehender Vertrag',
            value: CpProvisionSimulationComponent.MODE_EXISTING,
            icon: 'fa-regular fa-file-contract'
        }
    ];

    /** Mode A — the occupied dimensions plus contract data, premium and agent. */
    formWidgetMain: WidgetData;

    /** Mode A — the dimensions this Provisionsart constrains nowhere. Collapsed by default. */
    formWidgetExtra: WidgetData;

    /** Mode B — the contract and the settlement month. */
    formWidgetContract: WidgetData;

    /** Last values of the three forms. Kept regardless of validity; the gate is separate. */
    formValueMain: Record<string, unknown> = {};
    formValueExtra: Record<string, unknown> = {};
    formValueContract: Record<string, unknown> = {};

    /** Validity of the two gating forms. */
    mainFormValid: boolean = false;
    contractFormValid: boolean = false;

    /** Is the collapsed group of unoccupied dimensions open? */
    extraDimensionsVisible: boolean = false;

    /** The dimensions the Provisionsart constrains nowhere — the content of that group. */
    unoccupiedDimensions: ProvisionSimulationDimensionDto[] = [];

    /** The answer of the last simulation. */
    result: ProvisionSimulationResultDto;

    /** Transport failure of the last call — NOT a business outcome, which is always HTTP 200. */
    errorMessage: string;

    /** Resolved display name per dimension key, for the entered values of mode A. */
    dimensionValueNames: Map<string, string> = new Map<string, string>();

    /**
     * The live form groups of the two mode A forms, taken from `EVENT_NEW_FORM_GROUP`. Needed to
     * set and lock the derived Sparte/Kategorie, which may sit in either form depending on whether
     * this Provisionsart constrains them.
     */
    protected mainFormGroup: MvsFormGroup;
    protected extraFormGroup: MvsFormGroup;

    /**
     * The derivation edges of the current form, target key → source key, read off
     * `dimensions[].derivedFromKey`. Empty for a form definition without the property.
     */
    protected derivationEdges: Map<string, string> = new Map<string, string>();

    /**
     * Per target key: the source id its current state was derived from (`null` = source empty).
     * The guard against re-deriving on every keystroke and against a stale lookup answer
     * overtaking a newer selection.
     */
    protected derivedSourceIds: Map<string, number> = new Map<string, number>();

    /**
     * Per target key: the id this component itself set into the field. On release only a value
     * that still equals it is taken back — a value the user typed into a released field is theirs.
     */
    protected derivedValueIds: Map<string, number> = new Map<string, number>();

    protected readonly MODE_HYPOTHETICAL = CpProvisionSimulationComponent.MODE_HYPOTHETICAL;
    protected readonly MODE_EXISTING = CpProvisionSimulationComponent.MODE_EXISTING;

    /**
     * Object type alias per dimension key — the entity a dimension field points at, and the service
     * its display name is read from.
     *
     * All nine live in `cm`; the keys are exactly the ones the endpoint delivers. A key the map does
     * not know is skipped rather than guessed: an FK field on a wrong object type would offer a
     * list of the wrong things.
     */
    protected readonly dimensionObjectTypes: { [key: string]: string } = {
        partner: 'cm.Partner',
        contractType: 'cm.ContractType',
        group: 'cm.Group',
        contractOriginType: 'cm.ContractOriginType',
        product: 'cm.Product',
        category: 'cm.Category',
        contractBaseType: 'cm.ContractBaseType',
        contractBaseCategory: 'cm.ContractBaseCategory',
        contractClosureChannelType: 'cm.ContractClosureChannelType'
    };

    /**
     * FD-8 collision discriminator per dimension key — the attribute
     * {@link CpProvisionSimulationDimensionOptionsService} adds to a dimension's option query and
     * feeds into label disambiguation when two options are otherwise indistinguishable.
     *
     * Deliberately NOT populated for every dimension: only `cm.ContractType` is both known to
     * collide (measured — "Glasbruchschäden" exists twice, ids 47 and 54, 140 rows / 139 names) AND
     * has a `shortName` column to disambiguate with (`implements EntityShortName`). Five of the
     * other eight dimension entities (`cm.Group`, `cm.ContractOriginType`, `cm.Product`,
     * `cm.Category`, `cm.ContractBaseCategory`, `cm.ContractClosureChannelType`) have no `shortName`
     * column at all — requesting one would fail their QL query outright, not just do nothing. A key
     * missing here simply gets no discriminator; a plain collision (none measured) would then fall
     * straight to the id, same as `cp.ProvisionAgent` does once its own discriminator is exhausted.
     */
    protected readonly dimensionDiscriminatorAttributes: { [key: string]: string } = {
        contractType: 'shortName'
    };

    /**
     * The dimensions that are mandatory WHEN this Provisionsart constrains them.
     *
     * Every real contract carries a Gesellschaft and a Vertragsart — simulating without them
     * produces a combination that cannot occur, which is a misleading answer rather than a useful
     * one. The rule stays data-driven all the same: a Provisionsart that constrains neither gets
     * neither as a mandatory field.
     *
     * The origin (`contractOriginType`) is deliberately NOT in this list: Auflage D1 and
     * Spezifikation §2.2 both want it VISIBLE, not mandatory. Measured: 517.630 of 532.434 contracts
     * carry no origin at all, so a mandatory origin without an explicit "keine Herkunft" value would
     * make the commonest real case unsimulatable. It is therefore visible and optional, and carries
     * the description that names what it steers. Its position is the endpoint's delivery order
     * (partner, contractType, group, contractOriginType), not a choice made here: it is the first
     * OPTIONAL field only when `group` is either unoccupied or locked by its derivation.
     */
    protected readonly mandatoryWhenOccupied: string[] = ['partner', 'contractType'];

    /**
     * Attribute of the SOURCE entity that carries the implied target id, per target key. Two of
     * the three follow the `<targetKey>DtoId` convention; the Basiskategorie hangs off
     * `cm.ContractBaseType` as plain `category` (typed `ContractBaseCategory` on the entity), so the
     * map names it. A target the map does not know falls back to the convention.
     */
    protected readonly derivedSourceAttributes: { [targetKey: string]: string } = {
        group: 'groupDtoId',
        contractType: 'contractTypeDtoId',
        contractBaseCategory: 'categoryDtoId'
    };

    /**
     * Ties every subscription of this component to its lifetime: each `subscribe` below goes
     * through `takeUntilDestroyed(this.destroyRef)`, so a form definition, a simulation answer or
     * a name lookup that lands after the user has left the page is dropped instead of written into
     * a destroyed component. Same construction as `object-base-mode.component.ts` in core; no
     * hand-kept `Subscription` list and no empty `ngOnDestroy`.
     */
    protected readonly destroyRef: DestroyRef = inject(DestroyRef);

    /**
     * Resolved Bedienart (`select` + complete value list, or `autoCompleteFkey`) per dimension key,
     * plus {@link CP_SIMULATION_AGENT_OPTIONS_KEY} for the fixed "Berater" field. Populated once by
     * {@link loadDimensionOptions} before the first widget is ever built; {@link buildDimensionField}
     * and the agent field construction in {@link buildMainWidget} both read it through
     * {@link applyDimensionOptions}.
     */
    protected dimensionOptions: Map<string, ProvisionSimulationDimensionOptionsResultDto> =
        new Map<string, ProvisionSimulationDimensionOptionsResultDto>();

    constructor(
        protected coreService: MvsCoreService,
        protected simulationService: CpProvisionSimulationService,
        protected dimensionOptionsService: CpProvisionSimulationDimensionOptionsService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.loadDimensionOptions();
        this.initialized = true;
    }

    /**
     * Loads the value lists / degrade-to-`autoCompleteFkey` decision of every FKEY dimension
     * (G-10, FD-3) in ONE batched QL request, entirely in cp — the `FormService` is not touched.
     * `contractDtoId` (mode B, `cm.Contract`) is excluded on purpose: 532 434 rows keep the existing
     * `flexibleSearch` unconditionally (Auflage S-3), so it is never part of this request.
     *
     * Runs ONCE, independent of the chosen Provisionsart: which values `cm.Partner` or
     * `cp.ProvisionAgent` offer does not depend on it — only the mandatory/visible SET of dimensions
     * does, which comes from `simulationForm` separately. `loadProvisionTypes()` is deliberately
     * chained AFTER this resolves, so the very first widget build (a single Provisionsart is
     * auto-selected) never races an empty {@link dimensionOptions}.
     */
    protected loadDimensionOptions(): void {

        this.busy = true;

        const requests: ProvisionSimulationDimensionOptionsRequestDto[] = Object.keys(this.dimensionObjectTypes)
            .map(key => ({
                key,
                dataType: this.dimensionObjectTypes[key],
                discriminatorAttribute: this.dimensionDiscriminatorAttributes[key]
            }));

        this.dimensionOptionsService.loadOptions(requests)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (resolved: Map<string, ProvisionSimulationDimensionOptionsResultDto>) => {
                    this.dimensionOptions = resolved;
                    this.loadProvisionTypes();
                },
                error: () => {
                    // loadOptions() degrades internally and completes via `next` even on failure
                    // (V-16) — this branch is therefore not expected to fire, but the component
                    // must still become usable (on the safe, search-only control) if that
                    // guarantee is ever broken elsewhere.
                    this.dimensionOptions = new Map<string, ProvisionSimulationDimensionOptionsResultDto>();
                    this.loadProvisionTypes();
                }
            });
    }

    /**
     * Applies the loaded Bedienart of one FKEY dimension (G-10/G-6) to a field: a complete `select`
     * value list, or `autoCompleteFkey` when the row count is at or above the threshold — never the
     * implicit `flexibleSearch` the framework would otherwise pick for an unmarked `ManyToOne` field
     * (`mvs-form-field.component.ts:437-439`). {@link dimensionOptions} is always populated before
     * this runs (see {@link loadDimensionOptions}); a key still missing from it — which should not
     * happen — conservatively keeps the field on `autoCompleteFkey` rather than falling back to the
     * historic, not-searchable-by-name `flexibleSearch`.
     */
    protected applyDimensionOptions(field: MvsFormFieldDto, key: string): void {

        const options: ProvisionSimulationDimensionOptionsResultDto = this.dimensionOptions.get(key);

        if (!options || options.uiInputControl === 'autoCompleteFkey') {
            field.uiInputControl = 'autoCompleteFkey';
            return;
        }

        field.uiInputControl = 'select';
        field.createValueList(...(options.valueList ?? []));
    }

    /**
     * Loads the Provisionsarten that can be picked.
     *
     * No automatic pre-selection when there is more than one: which Provisionsart is the productive
     * one is a business fact this component must not guess — the reference tenant carries three, of
     * which two are inert. With exactly one there is nothing to choose and it is selected, which
     * saves a click without deciding anything.
     */
    protected loadProvisionTypes(): void {

        this.busy = true;

        const objectRequestList = ObjectRequestList.createBasic(false, [], [new Sorting('name', true)]);

        // Paging is the convention of every list request, however small the list: the reference
        // tenant carries three Provisionsarten, and a bound of 100 is far above any real configuration.
        objectRequestList.paging = PagingDto.createSize(100);

        this.coreService.getCrudService('cp.ProvisionType')
            .list(objectRequestList)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (dtoList: DtoList<ProvisionTypeDto>) => {
                    this.provisionTypes = dtoList?.entries ?? [];
                    this.busy = false;

                    if (this.provisionTypes.length === 1) {
                        this.selectedProvisionTypeId = this.provisionTypes[0].id;
                        this.handleProvisionTypeChange();
                    }
                },
                error: () => {
                    this.provisionTypes = [];
                    this.busy = false;
                    this.errorMessage = 'Die Provisionsarten konnten nicht geladen werden.';
                }
            });
    }

    /** A different Provisionsart means a different form and a stale result. */
    handleProvisionTypeChange(): void {

        this.simulationForm = null;
        this.formWidgetMain = null;
        this.formWidgetExtra = null;
        this.formWidgetContract = null;
        this.mainFormGroup = null;
        this.extraFormGroup = null;
        this.derivationEdges = new Map<string, string>();
        this.derivedSourceIds = new Map<string, number>();
        this.derivedValueIds = new Map<string, number>();
        this.result = null;
        this.errorMessage = null;
        this.formValueMain = {};
        this.formValueExtra = {};
        this.formValueContract = {};
        this.mainFormValid = false;
        this.contractFormValid = false;
        this.dimensionValueNames = new Map<string, string>();

        if (!this.selectedProvisionTypeId) {
            return;
        }

        this.busy = true;

        this.simulationService.getSimulationForm(this.selectedProvisionTypeId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
            next: (form: ProvisionSimulationFormDto) => {
                this.simulationForm = form;
                this.buildFormWidgets();
                this.busy = false;
            },
            error: () => {
                this.busy = false;
                this.errorMessage = 'Das Simulationsformular konnte nicht geladen werden.';
            }
        });
    }

    /** Switching the mode changes visibility only; a stale result is dropped. */
    handleModeChange(): void {
        this.result = null;
        this.errorMessage = null;
    }

    /** Opens or closes the group of dimensions this Provisionsart constrains nowhere. */
    handleToggleExtraDimensions(): void {
        this.extraDimensionsVisible = !this.extraDimensionsVisible;
    }

    // ────────────────────────────────────────────────────────────────────────────────────────────
    // The three transient forms
    // ────────────────────────────────────────────────────────────────────────────────────────────

    /**
     * Builds the three transient forms from the delivered form definition.
     *
     * Field ids match the request attributes so {@link handleSimulate} needs no renaming logic:
     * an FK field carries the framework's `…DtoId` suffix and is mapped by stripping it.
     */
    protected buildFormWidgets(): void {

        const dimensions: ProvisionSimulationDimensionDto[] = this.simulationForm?.dimensions ?? [];

        const occupied = dimensions.filter(dimension => dimension?.occupied);
        this.unoccupiedDimensions = dimensions.filter(dimension => !dimension?.occupied);

        // The derivation edges are data: only what the form names, and only between dimensions
        // this component has an object type for. The property is an explicit `null` on every
        // non-derived dimension, so the test is `!= null`, never the presence of the key.
        this.derivationEdges = new Map<string, string>();

        for (const dimension of dimensions) {
            if (dimension?.derivedFromKey != null && this.dimensionObjectTypes[dimension.derivedFromKey]) {
                this.derivationEdges.set(dimension.key, dimension.derivedFromKey);
            }
        }

        this.formWidgetMain = this.buildMainWidget(occupied);
        this.formWidgetExtra = this.buildExtraWidget(this.unoccupiedDimensions);
        this.formWidgetContract = this.buildContractWidget();
    }

    /** Mode A, main form: the occupied dimensions, the contract data, the premium and the agent. */
    protected buildMainWidget(occupied: ProvisionSimulationDimensionDto[]): WidgetData {

        const form = new MvsFormDto();
        const formFields: MvsFormFieldDto[] = [];

        let order: number = 10;

        for (const dimension of occupied) {

            const field = this.buildDimensionField(dimension, order);

            if (field) {
                formFields[field.id] = field;
                order = order + 10;
            }
        }

        // ── Contract data. The start date is the relevance key date, the event date and the start
        //    of the liability all at once; the protocol shows the three side by side afterwards.
        const startDateField = MvsFormFieldDto.createBasic('startDate', 'date', 'Vertragsbeginn');
        startDateField.uiMandatory = true;
        startDateField.uiDescription = 'Stichtag für Relevanz, Ereignis und Haftungsbeginn.';
        startDateField.order = 200;
        formFields['startDate'] = startDateField;

        // The term is MANDATORY, not optional: 275 of the 657 matrix rows of the reference tenant
        // calculate in per mille and need the evaluation sum, 238 carry a term band. Without it the
        // simulation matches only wildcard bands and per mille ends in M-006 — a result that looks
        // like a configuration error but is a missing input. Pre-filled with `defaultTermMonths`
        // through the widget hook, see {@link applyMainFormDefaults}.
        const termField = MvsFormFieldDto.createBasic('termMonths', 'numberInt', 'Laufzeit (Monate)');
        termField.uiMandatory = true;
        termField.uiNumberMinValue = 1;
        termField.uiDescription =
            'Pflichtangabe: Laufzeitbänder und die Promille-Berechnung brauchen sie. Vorbelegung: '
            + (this.simulationForm?.defaultTermMonths ?? CpProvisionSimulationComponent.FALLBACK_TERM_MONTHS)
            + ' Monate.';
        termField.order = 210;
        formFields['termMonths'] = termField;

        const applicationDateField = MvsFormFieldDto.createBasic('applicationDate', 'date', 'Antragsdatum');
        applicationDateField.uiDescription = 'Vorbelegung: Vertragsbeginn.';
        applicationDateField.order = 220;
        formFields['applicationDate'] = applicationDateField;

        const premiumField = MvsFormFieldDto.createBasic('premiumAmount', 'amount', 'Beitrag je Zahlungsperiode');
        premiumField.uiMandatory = true;
        premiumField.uiDoubleMinValue = 0;
        premiumField.order = 230;
        formFields['premiumAmount'] = premiumField;

        const frequencyField = MvsFormFieldDto.createBasic('paymentFrequency', 'select', 'Zahlweise');
        frequencyField.uiMandatory = true;
        frequencyField.uiDescription = 'Vorbelegung: monatlich.';
        frequencyField.order = 240;
        frequencyField.createValueList(
            ...PROVISION_PAYMENT_FREQUENCY_OPTIONS.map(option =>
                MvsFormValueListEntryDto.createBasic(option.value, option.label)));
        formFields['paymentFrequency'] = frequencyField;

        // ── The price block, all four fields OPTIONAL.
        //
        //    Until they existed, mode A wrote the one premium into gross, net and payment amount at
        //    once and left fee and tax rate empty, so the two net premium price references had
        //    nothing to read and answered nothing. Each field filled in here replaces that fallback
        //    for its own value; a field left empty keeps the previous behaviour exactly.
        //
        //    The gross price, the fee and the payment amount are money and are therefore built like
        //    the premium above ('amount'). The tax is a RATE and is deliberately NOT: it renders as
        //    a plain decimal, and both its label and its description say percent, because a rate
        //    mistaken for an amount produces a wrong net premium without any error anywhere.
        const priceGrossField = MvsFormFieldDto.createBasic('priceGross', 'amount', 'Bruttobeitrag');
        priceGrossField.uiDoubleMinValue = 0;
        priceGrossField.uiDescription =
            'Optional. Ohne Angabe wird der Beitrag je Zahlungsperiode verwendet.';
        priceGrossField.order = 242;
        formFields['priceGross'] = priceGrossField;

        const feeAmountField = MvsFormFieldDto.createBasic('feeAmount', 'amount', 'Gebühren');
        feeAmountField.uiDoubleMinValue = 0;
        feeAmountField.uiDescription =
            'Optional. Enthaltener Gebührenanteil — er ist nicht provisionsfähig.';
        feeAmountField.order = 244;
        formFields['feeAmount'] = feeAmountField;

        const paymentAmountField = MvsFormFieldDto.createBasic('paymentAmount', 'amount', 'Zahlbeitrag');
        paymentAmountField.uiDoubleMinValue = 0;
        paymentAmountField.uiDescription =
            'Optional. Tatsächlich gezahlter Betrag der Zahlungsperiode.';
        paymentAmountField.order = 246;
        formFields['paymentAmount'] = paymentAmountField;

        const taxField = MvsFormFieldDto.createBasic('tax', 'numberDecimal', 'Versicherungsteuersatz (%)');
        taxField.uiDescription =
            'Optional. Prozentsatz, kein Betrag — z. B. 19 für 19 %.';
        taxField.order = 248;
        formFields['tax'] = taxField;

        // ── The agent. "Berater" is the am.Agent; what is picked here is their Abrechnungskonto
        //    (cp.ProvisionAgent), because that is what the commission is booked against.
        const agentField = new MvsFormFieldDto();
        agentField.id = 'mainProvisionAgentDtoId';
        agentField.dataType = 'cp.ProvisionAgent';
        agentField.relationship = 'ManyToOne';
        agentField.access = 'update';
        agentField.uiLabel = 'Berater — abgeschlossen durch';
        agentField.uiDescription =
            'Abrechnungskonto des Beraters. Seine Vermittlergruppe entscheidet die Matrixzeile.';
        agentField.uiMandatory = true;
        agentField.order = 250;
        this.applyDimensionOptions(agentField, CP_SIMULATION_AGENT_OPTIONS_KEY);
        formFields['mainProvisionAgentDtoId'] = agentField;

        const settlementField = MvsFormFieldDto.createBasic('settlementMonth', 'date', 'Abrechnungsmonat');
        settlementField.uiDescription =
            'Stichtag der Haftungsfortschreibung. Vorbelegung: laufender Monat.';
        settlementField.order = 260;
        formFields['settlementMonth'] = settlementField;

        form.formFields = formFields;

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: Transient capture form of the provision simulation, mode "angenommener Vertrag".
         *         The MvsFormDto is built in the frontend from GET /cp/provisionTypes/{id}/simulationForm;
         *         the visible dimensions are the ones this provision type constrains. The write goes
         *         nowhere — POST .../simulate calculates without booking.
         */
        return WidgetFactory.createWidget('cp.simulation.provisionType.form.main',
            'Simulationseingaben', 'form', 'transient', 'transient', 'cp.ProvisionType',
            WidgetDataParam.create('size', 'S'), WidgetDataParam.create('form', form));
    }

    /**
     * Mode A, second form: the dimensions no row of this Provisionsart constrains.
     *
     * They are kept enterable rather than dropped — a dimension that constrains nothing today may
     * constrain something tomorrow, and a user who deliberately sets one gets an answer that says
     * so. They are collapsed because a field that changes no result competes for attention with the
     * ones that do.
     */
    protected buildExtraWidget(unoccupied: ProvisionSimulationDimensionDto[]): WidgetData {

        if (!unoccupied?.length) {
            return null;
        }

        const form = new MvsFormDto();
        const formFields: MvsFormFieldDto[] = [];

        let order: number = 10;

        for (const dimension of unoccupied) {

            const field = this.buildDimensionField(dimension, order, true);

            if (field) {
                formFields[field.id] = field;
                order = order + 10;
            }
        }

        form.formFields = formFields;

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: Transient capture form for the contract dimensions this provision type constrains
         *         nowhere. Optional throughout; collapsed by default; no write path.
         */
        return WidgetFactory.createWidget('cp.simulation.provisionType.form.extra',
            'Weitere Dimensionen', 'form', 'transient', 'transient', 'cp.ProvisionType',
            WidgetDataParam.create('size', 'S'), WidgetDataParam.create('form', form));
    }

    /** Mode B: the existing contract and the settlement month. */
    protected buildContractWidget(): WidgetData {

        const form = new MvsFormDto();
        const formFields: MvsFormFieldDto[] = [];

        const contractField = new MvsFormFieldDto();
        contractField.id = 'contractDtoId';
        contractField.dataType = 'cm.Contract';
        contractField.relationship = 'ManyToOne';
        contractField.access = 'update';
        contractField.uiLabel = 'Vertrag';
        contractField.uiDescription =
            'Der bestehende Vertrag wird als ERSTBERECHNUNG durchgerechnet — ohne den vorhandenen '
            + 'Provisionsbestand. Suche per VSNR im Reiter „via Vertragsdaten“.';
        contractField.uiMandatory = true;
        contractField.order = 10;
        formFields['contractDtoId'] = contractField;

        const settlementField = MvsFormFieldDto.createBasic('settlementMonth', 'date', 'Abrechnungsmonat');
        settlementField.uiDescription =
            'Stichtag der Haftungsfortschreibung. Vorbelegung: laufender Monat.';
        settlementField.order = 20;
        formFields['settlementMonth'] = settlementField;

        form.formFields = formFields;

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: Transient capture form of the provision simulation, mode "bestehender Vertrag".
         *         The contract is recalculated from scratch; nothing is booked and nothing is
         *         changed on the contract.
         */
        return WidgetFactory.createWidget('cp.simulation.provisionType.form.contract',
            'Bestehender Vertrag', 'form', 'transient', 'transient', 'cp.ProvisionType',
            WidgetDataParam.create('size', 'S'), WidgetDataParam.create('form', form));
    }

    /**
     * One dimension as an FK form field, or `null` for a key this component has no object type for.
     *
     * The Bedienart is applied via {@link applyDimensionOptions}: a complete `select` value list
     * (G-6) or `autoCompleteFkey` (G-10) — resolved from the batched {@link loadDimensionOptions}
     * load, never a hardcoded per-entity mapping.
     */
    protected buildDimensionField(dimension: ProvisionSimulationDimensionDto, order: number,
                                  forceOptional: boolean = false): MvsFormFieldDto {

        const objectType: string = this.dimensionObjectTypes[dimension?.key];

        if (!objectType) {
            // DS4 (a): a dimension key the endpoint delivers that this component has no object type
            // for used to be skipped SILENTLY. It is still skipped — inventing an object type would
            // offer a list of the wrong things — but the skip is now visible instead of a field
            // quietly missing from the form with no trace of why.
            console.warn(
                'cp-provision-simulation: unbekannter Dimensionsschlüssel "' + dimension?.key
                + '" wird übersprungen (kein Objekttyp in dimensionObjectTypes konfiguriert).');
            return null;
        }

        const field = new MvsFormFieldDto();
        field.id = dimension.key + 'DtoId';
        field.dataType = objectType;
        field.relationship = 'ManyToOne';
        field.access = 'update';
        field.uiLabel = dimension.label;
        field.order = order;
        field.uiMandatory = !forceOptional && this.mandatoryWhenOccupied.indexOf(dimension.key) >= 0;

        this.applyDimensionOptions(field, dimension.key);

        if (dimension.key === 'contractOriginType') {
            field.uiDescription =
                'Steuert die Zusatzkomponenten (Neukundenaktion, Umfrageaktion, Kaltakquise). '
                + 'Ohne Herkunft rechnet die Simulation nur die Basisprovision.';
        }

        if (dimension.derivedFromKey != null) {
            field.uiDescription = this.releasedDescription(dimension.key);
        }

        return field;
    }

    // ────────────────────────────────────────────────────────────────────────────────────────────
    // Form events
    // ────────────────────────────────────────────────────────────────────────────────────────────

    /**
     * Values and validity of the mode A main form.
     *
     * The first event of a freshly built form is `EVENT_NEW_FORM_GROUP`, which carries the group
     * itself; that is where the defaults go in. Every later event is a value change, and each one
     * re-evaluates the derivation edges.
     */
    handleMainFormEvent(event: WidgetGenericEvent): void {

        if (event?.eventName === MvsWidgetFormGenericEventConstants.EVENT_NEW_FORM_GROUP) {
            this.mainFormGroup = event.object as MvsFormGroup;
            this.applyMainFormDefaults(this.mainFormGroup);
            return;
        }

        const formGroup: MvsFormGroup = this.formGroupOf(event);

        if (!formGroup) {
            return;
        }

        this.mainFormGroup = formGroup;
        this.formValueMain = formGroup.value ?? {};
        this.mainFormValid = formGroup.status === 'VALID';
        this.errorMessage = null;

        this.deriveDimensions();
    }

    /** Values of the collapsed group. Every field there is optional, so validity is not gated. */
    handleExtraFormEvent(event: WidgetGenericEvent): void {

        if (event?.eventName === MvsWidgetFormGenericEventConstants.EVENT_NEW_FORM_GROUP) {
            this.extraFormGroup = event.object as MvsFormGroup;
            return;
        }

        const formGroup: MvsFormGroup = this.formGroupOf(event);

        if (!formGroup) {
            return;
        }

        this.extraFormGroup = formGroup;
        this.formValueExtra = formGroup.value ?? {};

        // A source dimension may live in this form as well as in the main one.
        this.deriveDimensions();
    }

    /** Values and validity of the mode B form. */
    handleContractFormEvent(event: WidgetGenericEvent): void {

        const formGroup: MvsFormGroup = this.formGroupOf(event);

        if (!formGroup) {
            return;
        }

        this.formValueContract = formGroup.value ?? {};
        this.contractFormValid = formGroup.status === 'VALID';
        this.errorMessage = null;
    }

    /** The form group of a widget event, or `null` for any other generic event. */
    protected formGroupOf(event: WidgetGenericEvent): MvsFormGroup {

        if (event?.eventName !== MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE) {
            return null;
        }

        const attribute: string = MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE_ATTR_FORM_GROUP;

        return event.object[attribute];
    }

    // ────────────────────────────────────────────────────────────────────────────────────────────
    // Pre-fill and derived dimensions
    // ────────────────────────────────────────────────────────────────────────────────────────────

    /**
     * Patches the defaults of the main form into a freshly built form group: the term from
     * `defaultTermMonths` (36 on the server, `40 §2`) and the payment frequency MONTHLY.
     *
     * Only EMPTY controls are filled, which is the repeat guard: a form group the widget rebuilds
     * with values already in it keeps them. The patch emits one value change, so the defaults
     * arrive in {@link formValueMain} through the same path as a user entry.
     */
    protected applyMainFormDefaults(formGroup: MvsFormGroup): void {

        if (!formGroup?.controls) {
            return;
        }

        const defaults: Record<string, unknown> = {
            termMonths: this.simulationForm?.defaultTermMonths
                ?? CpProvisionSimulationComponent.FALLBACK_TERM_MONTHS,
            paymentFrequency: ProvisionPaymentFrequency.MONTHLY
        };

        const patch: Record<string, unknown> = {};

        for (const id of Object.keys(defaults)) {

            const current: unknown = formGroup.controls[id]?.value;

            if (formGroup.controls[id] && (current === null || current === undefined || current === '')) {
                patch[id] = defaults[id];
            }
        }

        if (Object.keys(patch).length) {
            formGroup.patchValue(patch);
        }
    }

    /**
     * Settles every derivation edge against the current values of both mode A forms.
     *
     * Per edge: a source that has not changed since the last evaluation is skipped; a changed
     * source first RELEASES the target, then — when set — reads the source entity and locks the
     * target to the implied id. The sequence release → lookup → set + lock is deliberate: the
     * flexible-search input resolves its display name when it is CREATED, and `mvs-form-field`
     * re-creates the input exactly when its `readOnly` flag flips, so the input shows the NEW name
     * also on a change from one source to another where the target was already locked. A chain
     * (Produkt → Vertragsart → Sparte) needs no special handling: locking the Vertragsart raises a
     * value change, which brings this method back for the Sparte.
     */
    protected deriveDimensions(): void {

        const values: Record<string, unknown> = {...this.formValueExtra, ...this.formValueMain};

        this.derivationEdges.forEach((sourceKey: string, targetKey: string) => {

            const sourceId: number = this.toNumber(values[sourceKey + 'DtoId']);

            if (this.derivedSourceIds.has(targetKey) && this.derivedSourceIds.get(targetKey) === sourceId) {
                return;
            }

            this.derivedSourceIds.set(targetKey, sourceId);
            this.releaseDerivedDimension(targetKey);

            if (sourceId != null) {
                this.lookupDerivedValue(targetKey, sourceKey, sourceId);
            }
        });
    }

    /**
     * Reads the source entity and locks the target to the id it carries. A lookup answer that
     * arrives after a newer selection is dropped; a failed lookup leaves the target released and
     * enterable — the server derives the same edge itself and refuses a contradiction.
     */
    protected lookupDerivedValue(targetKey: string, sourceKey: string, sourceId: number): void {

        const crudService: MvsCrudService = this.coreService.getCrudService(this.dimensionObjectTypes[sourceKey]);

        // A read by primary key: no list request, hence no ObjectRequestList and no paging here.
        crudService.get(sourceId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (source: DtoDetail) => {

                if (this.derivedSourceIds.get(targetKey) !== sourceId) {
                    return;
                }

                // Read as a plain attribute: the cm DTOs live in another feature group and are not
                // imported across that boundary — see {@link derivedSourceAttributes}.
                const attributes: Record<string, unknown> = source as unknown as Record<string, unknown>;
                const attribute: string = this.derivedSourceAttributes[targetKey] ?? (targetKey + 'DtoId');

                this.lockDerivedDimension(targetKey, this.toNumber(attributes?.[attribute]));
            },
            error: () => {
                // Released and enterable — see the method comment.
            }
        });
    }

    /**
     * Sets a derived value into its field and makes the field read-only. A source that carries no
     * value for the target (a contract base type without a category) leaves the target released.
     */
    protected lockDerivedDimension(targetKey: string, valueId: number): void {

        if (valueId == null) {
            return;
        }

        const formGroup: MvsFormGroup = this.formGroupOfField(targetKey + 'DtoId');
        const field: MvsFormFieldDto = formGroup?.getFieldById(targetKey + 'DtoId');

        if (!formGroup || !field) {
            return;
        }

        formGroup.controls[targetKey + 'DtoId'].setValue(valueId);
        this.derivedValueIds.set(targetKey, valueId);

        field.access = 'read';
        field.uiDescription = this.lockedDescription(targetKey);
    }

    /**
     * Makes a derived field enterable again and takes back the value this component set — but
     * only that one: a value that no longer equals it was entered by the user and stays.
     */
    protected releaseDerivedDimension(targetKey: string): void {

        const formGroup: MvsFormGroup = this.formGroupOfField(targetKey + 'DtoId');
        const field: MvsFormFieldDto = formGroup?.getFieldById(targetKey + 'DtoId');

        if (!formGroup || !field) {
            return;
        }

        const control = formGroup.controls[targetKey + 'DtoId'];
        const derivedValueId: number = this.derivedValueIds.get(targetKey);

        if (derivedValueId != null && this.toNumber(control.value) === derivedValueId) {
            control.setValue(null);
        }

        this.derivedValueIds.delete(targetKey);

        field.access = 'update';
        field.uiDescription = this.releasedDescription(targetKey);
    }

    /**
     * Field description of a locked derived dimension. "Dimension" carries the article so the
     * sentence stays correct for every source — the sources differ in grammatical gender (die
     * Vertragsart, das Produkt, die Basisart), which a template with a bare article cannot serve.
     */
    protected lockedDescription(targetKey: string): string {

        const sourceKey: string = this.derivationEdges.get(targetKey);

        return 'Aus der gewählten Dimension „' + this.dimensionLabel(sourceKey) + '“ übernommen.';
    }

    /** Field description of a released derived dimension, naming the source it will follow. */
    protected releasedDescription(targetKey: string): string {

        const sourceKey: string = this.derivationEdges.get(targetKey);

        return 'Wird übernommen, sobald „' + this.dimensionLabel(sourceKey) + '“ gewählt ist.';
    }

    /** German caption of a dimension from the form definition; the key itself when unknown. */
    protected dimensionLabel(key: string): string {

        const dimensions: ProvisionSimulationDimensionDto[] = this.simulationForm?.dimensions ?? [];
        const dimension: ProvisionSimulationDimensionDto = dimensions.find(candidate => candidate?.key === key);

        return dimension?.label ?? key ?? '';
    }

    /** The mode A form group that holds a field, or `null` when neither form has it. */
    protected formGroupOfField(fieldId: string): MvsFormGroup {

        if (this.mainFormGroup?.controls?.[fieldId]) {
            return this.mainFormGroup;
        }

        if (this.extraFormGroup?.controls?.[fieldId]) {
            return this.extraFormGroup;
        }

        return null;
    }

    // ────────────────────────────────────────────────────────────────────────────────────────────
    // Running the simulation
    // ────────────────────────────────────────────────────────────────────────────────────────────

    /** The action button is only active with a complete form of the active mode. */
    get submittable(): boolean {

        if (this.busy || !this.selectedProvisionTypeId || !this.simulationForm) {
            return false;
        }

        return this.mode === CpProvisionSimulationComponent.MODE_EXISTING ? this.contractFormValid : this.mainFormValid;
    }

    /**
     * Runs the simulation.
     *
     * EVERY business outcome arrives as HTTP 200 with a full protocol — `NOT_ELIGIBLE`, `NO_EVENT`,
     * `CONFIGURATION_ERROR` and `ERROR` included. They are therefore handled in `next` and rendered
     * as answers; only a transport or request failure reaches `error`.
     */
    handleSimulate(): void {

        if (!this.submittable) {
            return;
        }

        const request: ProvisionSimulationRequestDto = this.buildRequest();

        // The server refuses a negative gross price, fee or payment amount with HTTP 400, and the
        // transport error branch below can only say "Bitte Eingaben prüfen" — which names neither
        // the field nor the reason. The three fields carry no min validator either: the form layer
        // attaches `Validators.min` only for a min value ABOVE zero
        // (`mvs-form-control.service.ts:282`), so a min of 0 attaches nothing. Hence the check here,
        // naming the offending field. The tax is deliberately not checked: a negative rate is
        // allowed on the wire.
        const negativePriceMessage: string = this.negativePriceMessage(request);

        if (negativePriceMessage) {
            this.errorMessage = negativePriceMessage;
            return;
        }

        this.busy = true;
        this.result = null;
        this.errorMessage = null;

        this.simulationService.simulate(this.selectedProvisionTypeId, request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
            next: (result: ProvisionSimulationResultDto) => {
                this.result = result;
                this.busy = false;
                this.resolveDimensionNames();
            },
            error: () => {
                this.busy = false;
                this.errorMessage =
                    'Die Simulation konnte nicht ausgeführt werden. Bitte Eingaben prüfen.';
            }
        });
    }

    /**
     * German message naming the first money field of the price block that carries a negative value,
     * or null when all three are fine. An empty field is null, and `null < 0` is false, so an
     * untouched block passes. Mode B carries no price block and therefore always answers null.
     */
    protected negativePriceMessage(request: ProvisionSimulationRequestDto): string {

        if (request.priceGross < 0) {
            return 'Der Bruttobeitrag darf nicht negativ sein.';
        }

        if (request.feeAmount < 0) {
            return 'Die Gebühren dürfen nicht negativ sein.';
        }

        if (request.paymentAmount < 0) {
            return 'Der Zahlbeitrag darf nicht negativ sein.';
        }

        return null;
    }

    /** The request of the active mode. */
    protected buildRequest(): ProvisionSimulationRequestDto {

        if (this.mode === CpProvisionSimulationComponent.MODE_EXISTING) {
            return this.buildExistingContractRequest();
        }

        return this.buildHypotheticalRequest();
    }

    /** Mode A: dimensions from both forms, contract data, premium, price block, agent. */
    protected buildHypotheticalRequest(): ProvisionSimulationRequestDto {

        const values: Record<string, unknown> = {...this.formValueExtra, ...this.formValueMain};

        const request: ProvisionSimulationRequestDto = {
            startDate: this.toIsoDate(values['startDate']),
            applicationDate: this.toIsoDate(values['applicationDate']),
            termMonths: this.toNumber(values['termMonths']),
            premiumAmount: this.toNumber(values['premiumAmount']),
            settlementMonth: this.toIsoDate(values['settlementMonth']),
            agents: this.buildAgents(values)
        };

        // The price block. `toNumber` answers null for an empty field, and a null goes out as the
        // absent value the server falls back on — so an untouched block reproduces the historic
        // request byte for byte.
        request.priceGross = this.toNumber(values['priceGross']);
        request.feeAmount = this.toNumber(values['feeAmount']);
        request.paymentAmount = this.toNumber(values['paymentAmount']);
        request.tax = this.toNumber(values['tax']);

        // The payment frequency goes out as the ORDINAL — the enum name is refused with HTTP 400
        // (measured, see the service Javadoc). The field is mandatory and pre-filled with MONTHLY
        // by {@link applyMainFormDefaults}, so a valid form always carries one.
        request.paymentFrequency = this.toNumber(values['paymentFrequency']);

        for (const key of Object.keys(this.dimensionObjectTypes)) {
            request[key + 'Id'] = this.toNumber(values[key + 'DtoId']);
        }

        return request;
    }

    /** Mode B: the contract and the settlement month; the server ignores everything else. */
    protected buildExistingContractRequest(): ProvisionSimulationRequestDto {
        return {
            contractId: this.toNumber(this.formValueContract['contractDtoId']),
            settlementMonth: this.toIsoDate(this.formValueContract['settlementMonth'])
        };
    }

    /**
     * The agents of the request — today exactly the main role.
     *
     * The two other roles are deliberately not captured: measured on the reference tenant, all nine
     * commission components pay `main`, and the whole contract stock carries 0 `support` and 3
     * `inquired` links. A field that changes no result on any real constellation was cut from V1
     * (Auflage D2); the wire format takes a list, so adding them later changes this method only.
     */
    protected buildAgents(values: Record<string, unknown>): ProvisionSimulationAgentDto[] {

        const provisionAgentId: number = this.toNumber(values['mainProvisionAgentDtoId']);

        if (provisionAgentId == null) {
            return [];
        }

        // The role goes out as the ORDINAL — the enum name is refused with HTTP 400 (measured).
        return [{role: ProvisionAgentRoleEnum.main, provisionAgentId}];
    }

    /**
     * Resolves the display names of dimension values that arrived WITHOUT one.
     *
     * A current backend fills `valueId` and `value` in BOTH modes from the record the engine
     * calculated with (mode B: the real contract row), so this method finds nothing to do. It is
     * the deploy-window fallback: a backend older than the name field sends the id alone, and a
     * protocol that says "Gesellschaft: 2292" answers nothing — so for exactly that case the name
     * of the id is READ from the dimension's own CRUD service through the framework's own name
     * resolution. That is a lookup, not a calculation, and it is what Spezifikation §2.3 allows
     * the frontend to do. A dimension the record leaves empty carries neither id nor name and gets
     * nothing invented for it.
     *
     * A failed lookup leaves the id standing rather than blanking the row: a visible id is a worse
     * caption but a true one.
     */
    protected resolveDimensionNames(): void {

        this.dimensionValueNames = new Map<string, string>();

        for (const dimension of this.result?.dimensions ?? []) {

            if (dimension?.valueId == null) {
                continue;
            }

            // Prefer what the server sent; the lookup below is only for a response without it.
            if (dimension.value) {
                this.dimensionValueNames.set(dimension.key, dimension.value);
                continue;
            }

            const objectType: string = this.dimensionObjectTypes[dimension.key];

            if (!objectType) {
                continue;
            }

            const crudService: MvsCrudService = this.coreService.getCrudService(objectType);

            // A read by primary key: no list request, hence no ObjectRequestList and no paging here.
            crudService.get(dimension.valueId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: (dto: DtoDetail) => {
                    const name: string = MvsFormFieldHelper.getObjectNameFromDto(crudService, dto);
                    if (name) {
                        this.dimensionValueNames.set(dimension.key, name);
                    }
                },
                error: () => {
                    // Keep the id visible — see the method comment.
                }
            });
        }
    }

    /**
     * What to show for one dimension of the answer.
     *
     * Three distinct statements, and none of them may look like another: a resolved name, a bare id
     * whose name could not be read, and "no value at all" — a dimension the calculated record
     * leaves empty, in either mode.
     */
    dimensionDisplayValue(dimension: ProvisionSimulationDimensionDto): string {

        if (dimension?.valueId == null) {
            return dimension?.value ?? '—';
        }

        return this.dimensionValueNames.get(dimension.key) ?? ('Id ' + dimension.valueId);
    }

    /** German rendering of one key-date figure. Formatting only. */
    formatKeyDate(value: string, unit: string): string {
        return formatProvisionSimulationValue(value, unit);
    }

    /** Inline colour of a chip from the backend's own tokens — the shared rule, see the presentation helper. */
    chipStyle(backgroundColor: string, color: string): { [key: string]: string } {
        return provisionSimulationChipStyle(backgroundColor, color);
    }

    /**
     * What a non-COMPLETED state MEANS and what can be changed about it — in German, next to the
     * state chip.
     *
     * None of these is an error, and an empty page would be the worst possible answer to any of
     * them. The sentences name the input the reader can move, because "die Konfiguration sagt nein"
     * without a lever is a dead end. Keyed by the FE enum constant; `COMPLETED` has no entry.
     */
    protected readonly stateExplanations: { [state: number]: string } = {
        [ProvisionSimulationState.NOT_ELIGIBLE]:
            'Die Provisionsrelevanz (K-05) verneint diesen Vertrag — es entsteht kein '
            + 'Provisionssatz. Häufigster Grund ist ein Vertragsbeginn vor dem Go-live der '
            + 'Provisionsart. Der Schritt "Provisionsrelevanz" unten zeigt die geprüften Zeilen.',
        [ProvisionSimulationState.NO_EVENT]:
            'Für diesen Vertrag entsteht kein Provisionsereignis. Die Provisionsart hat für '
            + 'diese Konstellation kein Ereignis konfiguriert — zu ändern ist das in der '
            + 'Konfiguration der Provisionsart, nicht in den Eingaben.',
        [ProvisionSimulationState.CONFIGURATION_ERROR]:
            'Die Provisionsart ist unvollständig konfiguriert und kann so nichts buchen. '
            + 'Die Meldungen unten nennen, was fehlt; die Eingaben zu ändern hilft hier nicht.',
        [ProvisionSimulationState.ERROR]:
            'Die Rechenkette hat einen Fehler gemeldet und das Ereignis eingefroren. Die '
            + 'Meldungen unten tragen den Code und den Text, den auch ein Provisionslauf '
            + 'schreiben würde.'
    };

    /** The explanation of the current state, or `null` for `COMPLETED` and for no result. */
    get stateExplanation(): string {

        const state: ProvisionSimulationState = this.resolvedState;

        return state == null ? null : (this.stateExplanations[state] ?? null);
    }

    /** The state of the answer as the FE enum constant — the shared rule, see the presentation helper. */
    protected get resolvedState(): ProvisionSimulationState {
        return provisionSimulationResolvedState(this.result?.state);
    }

    /** `Date` or string -> `yyyy-MM-dd`; `null` stays `null`. */
    protected toIsoDate(value: unknown): string {

        if (!value) {
            return null;
        }

        const date: Date = value instanceof Date ? value : new Date(value as string | number);

        if (isNaN(date.getTime())) {
            return null;
        }

        const month: string = `${date.getMonth() + 1}`.padStart(2, '0');
        const day: string = `${date.getDate()}`.padStart(2, '0');

        return `${date.getFullYear()}-${month}-${day}`;
    }

    /** Form value -> number; empty and unparseable stay `null` so the field is simply omitted. */
    protected toNumber(value: unknown): number {

        if (value === null || value === undefined || value === '') {
            return null;
        }

        const parsed: number = Number(value);

        return isNaN(parsed) ? null : parsed;
    }

}
